import { Controller } from "@hotwired/stimulus"

// Mimics Users::NameGenerator#sanitize from Ruby backend
function generateUsername(input: string): string {
  let name = input.toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s-]/g, "")
  const strip = ["und", "gmbh", "co", "kg", "ag"]
  for (const word of strip) {
    name = name.replace(new RegExp(`([-\\s])${word}(\\s|-|$)`, "g"), " ")
  }
  return name.trim().replace(/\s+/g, "_").replace(/-+/g, "_").replace(/^_|_$/g, "")
}

const HIRE_LABELS = ["1-2", "3-5", "6-10", "11-25", "26-50", "51-100", "100+"] as const

export default class extends Controller {
  static values = {
    membershipType: String,
    employeeCount: { type: Number, default: 150 },
    organisationName: { type: String, default: "" },
    offerToken: { type: String, default: "" },
    jobAdPrice: { type: Number, default: 962.5 },
  }

  static targets = [
    "price",
    "priceMonthly",
    "breakdown",
    "breakdownRowTpl",
    "breakdownRowFreeTpl",
    "breakdownTotalTpl",
    "employeeInput",
    "hasBms",
    "bmsAddonRow",
    "needsAppDelivery",
    "needsCrawler",
    "smartlinksPossible",
    "smartlinkDialog",
    "promotionCode",
    "promotionCodeFeedback",
    "fromDate",
    "crmQuoteResponse",
    "form",
    "pricingPanel",
    "ctaLink",
    "plannedHires",
    "plannedHiresDisplay",
    "jobAdSlots",
    "partnerBanner",
    "partnerPrice",
    "selectTotalPrice",
    "ctaLinkPartner",
    "ctaLinkSelect",
    "jobAdCount",
    "selectOption",
  ]

  declare membershipTypeValue: string
  declare employeeCountValue: number
  declare organisationNameValue: string
  declare offerTokenValue: string
  declare jobAdPriceValue: number

  declare readonly priceTarget: HTMLElement
  declare readonly priceMonthlyTarget: HTMLElement
  declare readonly breakdownTarget: HTMLElement

  declare readonly employeeInputTarget: HTMLInputElement
  declare readonly hasEmployeeInputTarget: boolean

  declare readonly hasBmsTarget: HTMLInputElement
  declare readonly hasHasBmsTarget: boolean
  declare readonly bmsAddonRowTarget: HTMLElement
  declare readonly hasBmsAddonRowTarget: boolean

  declare readonly needsAppDeliveryTarget: HTMLInputElement
  declare readonly hasNeedsAppDeliveryTarget: boolean
  declare readonly needsCrawlerTarget: HTMLInputElement
  declare readonly hasNeedsCrawlerTarget: boolean

  declare readonly smartlinksPossibleTarget: HTMLInputElement
  declare readonly hasSmartlinksPossibleTarget: boolean
  declare readonly smartlinkDialogTarget: HTMLDialogElement
  declare readonly hasSmartlinkDialogTarget: boolean
  declare readonly promotionCodeTarget: HTMLInputElement
  declare readonly hasPromotionCodeTarget: boolean
  declare readonly promotionCodeFeedbackTarget: HTMLElement
  declare readonly hasPromotionCodeFeedbackTarget: boolean
  declare readonly fromDateTarget: HTMLInputElement
  declare readonly hasFromDateTarget: boolean

  declare readonly crmQuoteResponseTarget: HTMLInputElement
  declare readonly hasCrmQuoteResponseTarget: boolean
  declare readonly formTarget: HTMLFormElement
  declare readonly hasFormTarget: boolean
  declare readonly breakdownRowTplTarget: HTMLTemplateElement
  declare readonly hasBreakdownRowTplTarget: boolean
  declare readonly breakdownRowFreeTplTarget: HTMLTemplateElement
  declare readonly hasBreakdownRowFreeTplTarget: boolean
  declare readonly breakdownTotalTplTarget: HTMLTemplateElement
  declare readonly hasBreakdownTotalTplTarget: boolean
  declare readonly pricingPanelTarget: HTMLElement
  declare readonly hasPricingPanelTarget: boolean
  declare readonly ctaLinkTarget: HTMLAnchorElement
  declare readonly hasCtaLinkTarget: boolean

  declare readonly plannedHiresTarget: HTMLInputElement
  declare readonly hasPlannedHiresTarget: boolean
  declare readonly plannedHiresDisplayTarget: HTMLElement
  declare readonly hasPlannedHiresDisplayTarget: boolean
  declare readonly jobAdSlotsTarget: HTMLInputElement
  declare readonly hasJobAdSlotsTarget: boolean

  declare readonly partnerBannerTarget: HTMLElement
  declare readonly hasPartnerBannerTarget: boolean
  declare readonly partnerPriceTarget: HTMLElement
  declare readonly hasPartnerPriceTarget: boolean
  declare readonly selectTotalPriceTarget: HTMLElement
  declare readonly hasSelectTotalPriceTarget: boolean
  declare readonly ctaLinkPartnerTarget: HTMLAnchorElement
  declare readonly hasCtaLinkPartnerTarget: boolean
  declare readonly ctaLinkSelectTarget: HTMLAnchorElement
  declare readonly hasCtaLinkSelectTarget: boolean
  declare readonly jobAdCountTarget: HTMLElement
  declare readonly hasJobAdCountTarget: boolean
  declare readonly selectOptionTargets: HTMLInputElement[]
  declare readonly hasSelectOptionTarget: boolean

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private persistTimer: ReturnType<typeof setTimeout> | null = null
  private lastSelectNetto: number = 0
  private lastPartnerNetto: number = 0

  connect() {
    if (this.hasHasBmsTarget && this.hasBmsAddonRowTarget) {
      this.bmsAddonRowTarget.toggleAttribute("data-revealed", this.hasBmsTarget.checked)
    }
    if (this.hasEmployeeInputTarget) {
      const restored = parseInt(this.employeeInputTarget.value, 10)
      if (restored > 0) {
        this.employeeCountValue = restored
      }
    }
    if (this.employeeCountValue > 0) {
      this.fetchQuote()
    }
    if (this.offerTokenValue && this.hasCtaLinkTarget) {
      this.ctaLinkTarget.addEventListener("click", (e) => this.saveAndRedirect(e))
    }
    if (this.offerTokenValue && this.hasCtaLinkPartnerTarget) {
      this.ctaLinkPartnerTarget.addEventListener("click", (e) => this.saveAndRedirectPartner(e))
    }
    if (this.offerTokenValue && this.hasCtaLinkSelectTarget) {
      this.ctaLinkSelectTarget.addEventListener("click", (e) => this.saveAndRedirect(e))
    }
  }

  changeEmployeeCount() {
    if (!this.hasEmployeeInputTarget) return
    const raw = this.employeeInputTarget.value.trim()
    const val = raw === "" ? 0 : parseInt(raw, 10)
    if (!val || val <= 0) {
      this.employeeCountValue = 0
      this.showPrompt()
      return
    }
    this.employeeCountValue = val
    this.debouncedFetchQuote()
  }

  toggleBms() {
    if (!this.hasHasBmsTarget || !this.hasBmsAddonRowTarget) return
    const checked = this.hasBmsTarget.checked
    this.bmsAddonRowTarget.toggleAttribute("data-revealed", checked)
    if (!checked && this.hasNeedsAppDeliveryTarget) {
      this.needsAppDeliveryTarget.checked = false
    }
  }

  confirmSmartlinks() {
    if (!this.hasSmartlinksPossibleTarget) return
    if (this.smartlinksPossibleTarget.checked) {
      this.fetchQuote()
      return
    }
    if (this.hasSmartlinkDialogTarget) {
      this.smartlinkDialogTarget.showModal()
    } else {
      this.fetchQuote()
    }
  }

  confirmSmartlinkDisable() {
    if (this.hasSmartlinkDialogTarget) {
      this.smartlinkDialogTarget.close()
    }
    this.fetchQuote()
  }

  cancelSmartlinkDisable() {
    if (this.hasSmartlinkDialogTarget) {
      this.smartlinkDialogTarget.close()
    }
    if (this.hasSmartlinksPossibleTarget) {
      this.smartlinksPossibleTarget.checked = true
    }
  }

  updatePlannedHires() {
    if (!this.hasPlannedHiresTarget) return
    const idx = parseInt(this.plannedHiresTarget.value, 10)
    const label = HIRE_LABELS[idx] || HIRE_LABELS[0]
    if (this.hasPlannedHiresDisplayTarget) {
      this.plannedHiresDisplayTarget.textContent = label
    }
    this.persistOffer()
  }

  updateJobAdComparison() {
    this.updatePartnerComparison()
    this.persistOffer()
  }

  selectOptionChanged() {
    this.updatePartnerComparison()
    this.persistOffer()
  }

  persistOffer() {
    if (!this.offerTokenValue) return
    if (this.persistTimer) clearTimeout(this.persistTimer)
    this.persistTimer = setTimeout(() => {
      fetch(`/angebot/${this.offerTokenValue}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(this.buildOfferBody()),
      }).catch((error) => {
        console.error("Offer auto-persist failed:", error)
      })
    }, 500)
  }

  saveAndRedirect(event: Event) {
    event.preventDefault()
    const href = this.ctaLinkTarget.href

    fetch(`/angebot/${this.offerTokenValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ ...this.buildOfferBody(), finalize: true }),
    })
      .then(() => { window.location.href = href })
      .catch((error) => {
        console.error("Offer update failed:", error)
        window.location.href = href
      })
  }

  saveAndRedirectPartner(event: Event) {
    event.preventDefault()
    const href = this.ctaLinkPartnerTarget.href

    fetch(`/angebot/${this.offerTokenValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ ...this.buildOfferBody(), membership_model: "partner", finalize: true }),
    })
      .then(() => { window.location.href = href })
      .catch((error) => {
        console.error("Offer update failed:", error)
        window.location.href = href
      })
  }

  // Validates the code against the CRM before re-pricing, so an invalid code
  // gives feedback instead of silently leaving the price unchanged.
  checkPromotionCode() {
    if (!this.hasPromotionCodeTarget) return
    const code = this.promotionCodeTarget.value.trim()

    if (!code) {
      this.showPromotionFeedback("", false)
      this.fetchQuote()
      return
    }

    fetch("/registration/aktionscode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ promotion_code: code }),
    })
      .then(r => r.json())
      .then((data) => {
        this.showPromotionFeedback(
          data.valid ? (data.text || "Aktionscode wurde angewendet.") : "Aktionscode ist nicht gültig.",
          !!data.valid
        )
        this.fetchQuote()
      })
      .catch((error) => {
        console.error("Promotion code check failed:", error)
        this.fetchQuote()
      })
  }

  private showPromotionFeedback(message: string, ok: boolean) {
    if (!this.hasPromotionCodeFeedbackTarget) return
    const el = this.promotionCodeFeedbackTarget
    el.textContent = message
    el.hidden = !message
    el.classList.toggle("tw-form__error--ok", ok)
    if (this.hasPromotionCodeTarget) {
      this.promotionCodeTarget.classList.toggle("tw-form__input--error", !!message && !ok)
    }
  }

  fetchQuote() {
    const orgName = this.getOrganisationName()
    const username = generateUsername(orgName) || "unbekannt"

    const commonBody = {
      employee_count: this.employeeCountValue,
      smartlinks_possible: this.hasSmartlinksPossibleTarget ? this.smartlinksPossibleTarget.checked : true,
      addons: this.selectedAddons(),
      organisation_name: orgName || "Unbekannt",
      username: username,
      start_date: this.hasFromDateTarget ? this.fromDateTarget.value : "",
      promotion_code: this.hasPromotionCodeTarget ? this.promotionCodeTarget.value : "",
      akquisition_channel: "",
    }

    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfToken(),
    }

    const container = this.loadingElement()
    container.classList.add("pricing-card--loading")

    const selectFetch = fetch("/registration/quote", {
      method: "POST",
      headers,
      body: JSON.stringify({ ...commonBody, membership_type: this.membershipTypeValue }),
    })
      .then(r => {
        if (!r.ok) throw new Error(`Quote request failed: ${r.status} ${r.statusText}`)
        return r.json()
      })

    const partnerFetch = this.shouldComparePartner()
      ? fetch("/registration/quote", {
          method: "POST",
          headers,
          body: JSON.stringify({ ...commonBody, membership_type: "partner" }),
        })
          .then(r => {
            if (!r.ok) throw new Error(`Partner quote failed: ${r.status} ${r.statusText}`)
            return r.json()
          })
          .catch(() => null)
      : Promise.resolve(null)

    Promise.all([selectFetch, partnerFetch])
      .then(([selectData, partnerData]) => {
        this.lastSelectNetto = selectData.totalPrice?.netto ?? 0
        this.updatePriceDisplay(selectData)
        if (this.hasCrmQuoteResponseTarget) {
          this.crmQuoteResponseTarget.value = JSON.stringify(selectData)
        }
        if (this.hasCtaLinkTarget) {
          this.updateCtaLink()
        }
        if (partnerData) {
          this.lastPartnerNetto = partnerData.totalPrice?.netto ?? 0
        }
        this.updatePartnerComparison()
      })
      .catch(error => {
        console.error("Quote fetch failed:", error)
      })
      .finally(() => {
        container.classList.remove("pricing-card--loading")
        this.persistOffer()
      })
  }

  private selectedAddons(): string[] {
    const addons: string[] = []
    if (this.hasNeedsCrawlerTarget && this.needsCrawlerTarget.checked) addons.push("crawler")
    if (this.hasNeedsAppDeliveryTarget && this.needsAppDeliveryTarget.checked) addons.push("bms_applicant_push")
    if (addons.includes("crawler") && addons.includes("bms_applicant_push")) {
      return ["bms_complete"]
    }
    return addons
  }

  private buildOfferBody(): Record<string, any> {
    const body: Record<string, any> = {
      employee_count: this.employeeCountValue,
      has_bms: this.hasHasBmsTarget ? this.hasBmsTarget.checked : false,
      needs_application_delivery: this.hasNeedsAppDeliveryTarget ? this.needsAppDeliveryTarget.checked : false,
      needs_crawler: this.hasNeedsCrawlerTarget ? this.needsCrawlerTarget.checked : false,
    }
    if (this.hasPlannedHiresTarget) {
      const idx = parseInt(this.plannedHiresTarget.value, 10)
      body.planned_hires = HIRE_LABELS[idx] || HIRE_LABELS[0]
    }
    if (this.hasJobAdSlotsTarget && this.jobAdSlotsTarget.value) {
      body.job_ad_slots = parseInt(this.jobAdSlotsTarget.value, 10)
    }
    if (this.hasSelectOptionTarget) {
      const checked = this.selectOptionTargets.find(r => r.checked)
      if (checked) body.select_option = checked.value
    }
    if (this.hasPromotionCodeTarget) {
      body.promotion_code = this.promotionCodeTarget.value.trim()
    }
    return body
  }

  private debouncedFetchQuote() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchQuote(), 300)
  }

  private getOrganisationName(): string {
    if (this.organisationNameValue) return this.organisationNameValue
    if (this.hasFormTarget) {
      const el = this.formTarget.querySelector<HTMLInputElement>('[name="registration[organisation_name]"]')
      return el?.value || ""
    }
    return ""
  }

  private loadingElement(): HTMLElement {
    return this.hasPricingPanelTarget ? this.pricingPanelTarget : this.element as HTMLElement
  }

  private showPrompt() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.priceTarget.textContent = "Wie viele Mitarbeitende hat Ihr Unternehmen?"
    this.priceTarget.classList.add("pricing-card__price--prompt")
    this.priceMonthlyTarget.innerHTML = "\u00A0"
    this.breakdownTarget.textContent = ""
    if (this.hasCrmQuoteResponseTarget) {
      this.crmQuoteResponseTarget.value = ""
    }
  }

  private updatePriceDisplay(data: any) {
    const netto = data.totalPrice?.netto ?? 0
    const annual = this.formatCurrency(netto)
    const monthly = this.formatCurrency(Math.round(netto / 12))

    this.priceTarget.textContent = ""
    this.priceTarget.classList.remove("pricing-card__price--prompt")
    const cur = document.createElement("span")
    cur.className = "pricing-card__price-cur"
    cur.textContent = "\u20AC"
    const unit = document.createElement("span")
    unit.className = "pricing-card__price-unit"
    unit.textContent = " / Monat"
    this.priceTarget.append(cur, monthly, unit)
    this.priceMonthlyTarget.textContent = `${annual} \u20AC / Jahr`

    if (data.lineItems && this.hasBreakdownRowTplTarget) {
      this.breakdownTarget.textContent = ""
      let hasFreeItems = false

      data.lineItems.forEach((item: any) => {
        const val = item.nettoFormatted || this.formatCurrency(item.netto ?? 0) + " \u20AC"
        if (item.free && this.hasBreakdownRowFreeTplTarget) {
          hasFreeItems = true
          const row = this.cloneTemplate(this.breakdownRowFreeTplTarget)
          row.querySelector('[data-slot="label"]')!.textContent = item.position
          row.querySelector('[data-slot="value"]')!.textContent = val
          this.breakdownTarget.append(row)
        } else {
          const row = this.cloneTemplate(this.breakdownRowTplTarget)
          row.querySelector('[data-slot="label"]')!.textContent = item.position
          row.querySelector('[data-slot="value"]')!.textContent = val
          this.breakdownTarget.append(row)
        }
      })

      if (hasFreeItems && data.addonsFreeUntil) {
        const hint = document.createElement("div")
        hint.className = "pricing-card__footnote"
        hint.textContent = `* Bei Abschluss des Vertrages bis ${data.addonsFreeUntil}.`
        this.breakdownTarget.append(hint)
      }

      if (this.hasBreakdownTotalTplTarget) {
        const totalRow = this.cloneTemplate(this.breakdownTotalTplTarget)
        totalRow.querySelector('[data-slot="value"]')!.textContent = `${annual} \u20AC`
        this.breakdownTarget.append(totalRow)
      }
    }
  }

  private cloneTemplate(tpl: HTMLTemplateElement): DocumentFragment {
    return tpl.content.cloneNode(true) as DocumentFragment
  }

  private formatCurrency(value: number): string {
    return value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  private updateCtaLink() {
    const base = this.membershipTypeValue === "partner" ? "/registrieren/partner" : "/registrieren/select"
    const qs = this.offerTokenValue ? `?offer_token=${encodeURIComponent(this.offerTokenValue)}` : ""
    this.ctaLinkTarget.href = `${base}${qs}`
    if (this.hasCtaLinkSelectTarget) {
      this.ctaLinkSelectTarget.href = `${base}${qs}`
    }
    if (this.hasCtaLinkPartnerTarget) {
      const partnerQs = this.offerTokenValue ? `?offer_token=${encodeURIComponent(this.offerTokenValue)}` : ""
      this.ctaLinkPartnerTarget.href = `/registrieren/partner${partnerQs}`
    }
  }

  private shouldComparePartner(): boolean {
    return !!(this.offerTokenValue && this.membershipTypeValue === "eb_select" && this.hasPartnerBannerTarget)
  }

  private getJobAdSlots(): number {
    if (!this.hasJobAdSlotsTarget) return 0
    const val = parseInt(this.jobAdSlotsTarget.value, 10)
    return isNaN(val) ? 0 : val
  }

  private updatePartnerComparison() {
    if (!this.hasPartnerBannerTarget) return
    const slots = this.getJobAdSlots()
    const extraSlots = Math.max(0, slots - 1)
    const selectTotal = this.lastSelectNetto + extraSlots * this.jobAdPriceValue
    if (selectTotal >= this.lastPartnerNetto && this.lastPartnerNetto > 0 && slots > 0) {
      this.showPartnerBanner(selectTotal, this.lastPartnerNetto)
    } else {
      this.hidePartnerBanner()
    }
  }

  private showPartnerBanner(selectTotal: number, partnerPrice: number) {
    this.partnerBannerTarget.hidden = false
    if (this.hasSelectTotalPriceTarget) {
      this.selectTotalPriceTarget.textContent = `${this.formatCurrency(selectTotal)} \u20AC`
    }
    if (this.hasPartnerPriceTarget) {
      this.partnerPriceTarget.textContent = `${this.formatCurrency(partnerPrice)} \u20AC`
    }
    if (this.hasJobAdCountTarget) {
      this.jobAdCountTarget.textContent = String(this.getJobAdSlots())
    }
    if (this.hasCtaLinkTarget) {
      this.ctaLinkTarget.hidden = true
    }
    if (this.hasCtaLinkSelectTarget) {
      this.ctaLinkSelectTarget.hidden = false
    }
    if (this.hasCtaLinkPartnerTarget) {
      this.ctaLinkPartnerTarget.hidden = false
    }
  }

  private hidePartnerBanner() {
    if (this.hasPartnerBannerTarget) {
      this.partnerBannerTarget.hidden = true
    }
    if (this.hasCtaLinkTarget) {
      this.ctaLinkTarget.hidden = false
    }
    if (this.hasCtaLinkSelectTarget) {
      this.ctaLinkSelectTarget.hidden = true
    }
    if (this.hasCtaLinkPartnerTarget) {
      this.ctaLinkPartnerTarget.hidden = true
    }
  }

  private csrfToken(): string {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
  }
}
